/**
 * Share destinations for a listing — pure URL builders so each one can be
 * asserted exactly in tests, plus the payload handed to the native share
 * sheet. Nothing here touches the DOM.
 */

import { listingPath } from '../../../util/urlHelpers';

// Order is the order of the buttons in the modal.
export const SHARE_TARGETS = ['facebook', 'messenger', 'whatsapp', 'x', 'pinterest', 'email'];

const enc = encodeURIComponent;

/**
 * Build the share URL for one platform, using each platform's official share
 * endpoint. Every URL opens in a new tab except email, which is a mailto:.
 *
 * @param {string} target - one of SHARE_TARGETS
 * @param {Object} params
 * @param {string} params.url - canonical listing URL
 * @param {string} params.title - listing title
 * @param {string} params.text - short share sentence
 * @param {string} [params.imageUrl] - cover image (Pinterest needs one)
 * @param {string} [params.facebookAppId] - required by Messenger's web dialog
 * @returns {string|null} the URL, or null when the platform can't be used
 *   (Messenger without an app id)
 */
export const shareUrlFor = (target, { url, title, text, imageUrl, facebookAppId }) => {
  switch (target) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
    case 'messenger':
      // Messenger's desktop share is a Facebook dialog and needs an app id.
      // On phones the OS share sheet reaches Messenger directly instead.
      return facebookAppId
        ? `https://www.facebook.com/dialog/send?app_id=${enc(facebookAppId)}&link=${enc(
            url
          )}&redirect_uri=${enc(url)}`
        : null;
    case 'whatsapp':
      return `https://wa.me/?text=${enc(`${text} ${url}`)}`;
    case 'x':
      return `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`;
    case 'pinterest':
      return `https://pinterest.com/pin/create/button/?url=${enc(url)}&description=${enc(
        title
      )}${imageUrl ? `&media=${enc(imageUrl)}` : ''}`;
    case 'email':
      return `mailto:?subject=${enc(title)}&body=${enc(`${text}\n\n${url}`)}`;
    default:
      return null;
  }
};

/** What the native share sheet gets. Platforms append the URL themselves. */
export const nativeSharePayload = ({ url, title, text }) => ({ title, text, url });

/**
 * The share URL is the listing's main address, /l/<slug>/<uuid> from its
 * current title: the same one the page publishes as canonical and og:url, and
 * the one the site links to. An older slug still opens the listing.
 */
export const canonicalListingUrl = (marketplaceRootURL, listingId, title) =>
  `${marketplaceRootURL}${listingPath(listingId, title)}`;

/**
 * The slugless profile URL, for the same reason: it is what the profile page
 * sets as og:url, and what a crawler keys its cached card on.
 */
export const canonicalProfileUrl = (marketplaceRootURL, userId) =>
  `${marketplaceRootURL}/u/${userId}`;
